import { ArrowDown, Download, Mail } from 'lucide-react';
import { motion, useReducedMotion } from 'motion/react';
import { profile } from '@/data';
import resumes from '@/data/resumes.json';
import { asset } from '@/lib/paths';
import { cn } from '@/lib/cn';
import { GithubIcon, LinkedinIcon } from '@/components/ui/BrandIcons';

const generic = resumes.find((entry) => entry.id === 'generic') ?? resumes[0];

export function Hero() {
  const reduceMotion = useReducedMotion();

  return (
    <section
      id="top"
      aria-labelledby="hero-heading"
      className="relative flex min-h-[88dvh] items-center overflow-hidden"
    >
      {/* Decorative wash. aria-hidden because it carries no information. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10 opacity-70"
        style={{
          background:
            'radial-gradient(60% 55% at 18% 12%, color-mix(in oklab, var(--primary) 22%, transparent), transparent 70%),' +
            'radial-gradient(50% 50% at 88% 78%, color-mix(in oklab, var(--accent) 18%, transparent), transparent 70%)',
        }}
      />

      <div className="mx-auto w-full max-w-4xl px-6 py-20">
        {/*
          whileInView rather than animate, so returning to the top replays the
          intro instead of showing a static block. It still fires on first load
          because the hero is in view at that point.
        */}
        <motion.div
          initial={reduceMotion ? false : { opacity: 0, y: 20 }}
          whileInView={reduceMotion ? undefined : { opacity: 1, y: 0 }}
          viewport={{ once: false, amount: 0.25 }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
        >
          <p className="text-sm font-medium tracking-wide text-primary uppercase">
            {profile.location}
          </p>

          <h1
            id="hero-heading"
            className="mt-3 text-4xl font-bold tracking-tight text-balance text-foreground sm:text-5xl lg:text-6xl"
          >
            {profile.name}
          </h1>

          <p className="mt-3 text-xl font-medium text-muted-foreground sm:text-2xl">
            {profile.title}
          </p>

          <p className="mt-6 max-w-2xl text-lg text-pretty text-foreground/90">{profile.tagline}</p>

          <div className="mt-9 flex flex-wrap items-center gap-3">
            <a
              href={asset(`resumes/${generic.files.pdf}`)}
              download
              className={cn(
                'inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold',
                'bg-primary text-primary-foreground transition-opacity hover:opacity-90',
              )}
            >
              <Download className="h-4 w-4" aria-hidden />
              Download resume
            </a>

            <a
              href="#contact"
              className={cn(
                'inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold',
                'border border-border text-foreground transition-colors hover:bg-muted',
              )}
            >
              <Mail className="h-4 w-4" aria-hidden />
              Get in touch
            </a>

            <div className="flex items-center gap-1">
              <IconLink href={profile.links.github} label="GitHub profile">
                <GithubIcon className="h-5 w-5" />
              </IconLink>
              <IconLink href={profile.links.linkedin} label="LinkedIn profile">
                <LinkedinIcon className="h-5 w-5" />
              </IconLink>
            </div>
          </div>
        </motion.div>

        <a
          href="#about"
          aria-label="Skip to About"
          className="mt-16 inline-flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
        >
          <ArrowDown className={cn('h-4 w-4', !reduceMotion && 'animate-bounce')} aria-hidden />
          More below
        </a>
      </div>
    </section>
  );
}

function IconLink({
  href,
  label,
  children,
}: {
  href: string;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <a
      href={href}
      target="_blank"
      // noreferrer alongside noopener: without it the target page can read
      // document.referrer, and rel=noopener alone does not prevent that.
      rel="noopener noreferrer"
      aria-label={label}
      className="inline-flex h-10 w-10 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
    >
      {children}
    </a>
  );
}
